import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Section } from "./Section";

import { createEnergyPlotGroup } from "@/constants/create";
import type { EnergyValueUnit } from "@/energyPlotView/const";
import type { EnergyPlot, EnergyPlotGroup } from "@/energyPlotView/types";
import { generateSiteLabel, resolvePreferredEnergyModel } from "@/energyPlotView/utils";
import { EditableTextbox } from "@/overlay/components/CommonParts/EditableTextbox";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { AddEnergyGroupModal } from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/AddEnergyGroupModal";
import style from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/AvailableEnergyForecastDataForm.module.scss";
import { EnergyGroupSettings } from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/EnergyGroupSettings";
import { useAvailableEnergyForecastConfig } from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/useAvailableEnergyForecastConfig";
import { ManageActionPanel } from "@/overlay/components/LayerStackWindow/ManageActionPanel/ManageActionPanel";
import { FormField } from "@/overlay/components/form-field";
import {
  addEnergyGroup,
  removeEnergyGroup,
  setEnergyForecastPlotProps,
  setEnergyGroupProps,
  setEnergyPlotProps,
  setEnergyPlotType,
} from "@/reducer/EnergyPlotsState";
import { MmCheckbox, MmDropdown } from "@mm/zeus-react";

interface PlotSettingProps {
  energyPlot: EnergyPlot;
  clearEnergyPlot: () => void;
}

export function EnergyPlotSetting({ clearEnergyPlot, energyPlot }: PlotSettingProps) {
  const dispatch = useDispatch();
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [selectedModelToAdd, setSelectedModelToAdd] = useState("");

  const handleUnitSwitch = (value: string) => {
    dispatch(
      setEnergyPlotProps({
        id: energyPlot.id,
        props: { valueUnit: value as EnergyValueUnit },
      }),
    );
  };
  const handleEnergyPlotChange = useCallback(
    (energyPlot: EnergyPlot) => {
      dispatch(setEnergyForecastPlotProps(energyPlot));
    },
    [dispatch],
  );
  const handleEnergyTypeChange = useCallback(
    (id: number, forecastType: string) => {
      dispatch(setEnergyPlotType({ id, forecastType }));
    },
    [dispatch],
  );
  const { availableModelNodes, forecastFieldProps, countryFieldProps, subSitesFieldProps, unitFieldProps } =
    useAvailableEnergyForecastConfig({
      energyPlot,
      onChange: handleEnergyPlotChange,
      onTypeChange: handleEnergyTypeChange,
      onUnitChange: handleUnitSwitch,
    });
  const selectedGroupModelIds = useMemo(
    () => new Set(energyPlot.groups.map((group) => group.model).filter((value): value is string => !!value)),
    [energyPlot.groups],
  );
  const availableModelNodesForAdd = useMemo(
    () => availableModelNodes.filter((node) => !!node.value && !selectedGroupModelIds.has(node.value)),
    [availableModelNodes, selectedGroupModelIds],
  );
  const availableModelNodesByGroup = useMemo(
    () =>
      energyPlot.groups.map((group, groupIndex) => {
        const selectedByOtherGroups = new Set(
          energyPlot.groups
            .filter((_, index) => index !== groupIndex)
            .map((groupNode) => groupNode.model)
            .filter((value): value is string => !!value),
        );

        return availableModelNodes.filter(
          (node) => !!node.value && (node.value === group.model || !selectedByOtherGroups.has(node.value)),
        );
      }),
    [availableModelNodes, energyPlot.groups],
  );
  const availableModelIds = useMemo(
    () => availableModelNodesForAdd.map((node) => node.value).filter((value): value is string => !!value),
    [availableModelNodesForAdd],
  );
  const hasAvailableModels = availableModelIds.length > 0;
  const defaultModelToAdd = useMemo(() => resolvePreferredEnergyModel(availableModelIds), [availableModelIds]);
  const handleGroupRemove = useCallback(
    (groupIndex: number) => {
      dispatch(removeEnergyGroup({ energyPlotId: energyPlot.id, groupIndex }));
    },
    [dispatch, energyPlot.id],
  );
  const handleGroupAdd = useCallback(
    (model: string, ensMembers?: string[]) => {
      dispatch(
        addEnergyGroup({
          energyPlotId: energyPlot.id,
          energyGroup: {
            ...createEnergyPlotGroup(model),
            ensMembers,
          },
        }),
      );
    },
    [dispatch, energyPlot.id],
  );
  const handleGroupUpdate = useCallback(
    (groupIndex: number, updatedGroup: Partial<EnergyPlotGroup>) => {
      dispatch(setEnergyGroupProps({ energyPlotId: energyPlot.id, groupIndex, energyGroup: updatedGroup }));
    },
    [dispatch, energyPlot.id],
  );
  const toggleAddGroupModal = useCallback(() => {
    setShowAddGroupModal((isOpen) => {
      const nextIsOpen = !isOpen;
      setSelectedModelToAdd(nextIsOpen ? defaultModelToAdd : "");
      return nextIsOpen;
    });
  }, [defaultModelToAdd]);
  const confirmAddGroup = useCallback(
    (ensMembers?: string[]) => {
      if (!selectedModelToAdd) {
        return;
      }

      handleGroupAdd(selectedModelToAdd, ensMembers);
      toggleAddGroupModal();
    },
    [handleGroupAdd, selectedModelToAdd, toggleAddGroupModal],
  );

  return (
    <>
      <div className="title-row">
        <EditableTextbox
          className={"editable-map-title"}
          placeholder={"Untitled Energy Plot"}
          value={energyPlot.title}
          blurOnEnterKey
          onSave={(newTitle: string) => {
            dispatch(
              setEnergyForecastPlotProps({
                ...energyPlot,
                title: newTitle,
              }),
            );
          }}
        />
        <ManageActionPanel onRemove={clearEnergyPlot} />
      </div>

      <div className={classNames("expansion-panel box-section--breakout actionable", "expansion-panel--expanded")}>
        <Section
          tag={t`Energy Forecast`}
          title={energyPlot.forecastType === "wlp" ? t`Wind power` : t`Solar power`}
          defaultOpen
        >
          <FormField label={t`Forecast`}>
            <MmDropdown key={energyPlot.forecastType} {...forecastFieldProps} placeholder="Select a forecast type" />
          </FormField>
          <FormField label={t`Unit`}>
            <MmDropdown
              key={energyPlot.valueUnit}
              {...unitFieldProps}
              data-testid={"forecast-unit-select"}
              placeholder="Select unit GW or MW"
            />
          </FormField>
          <FormField label={t`Country`}>
            <MmDropdown
              key={energyPlot.siteId}
              {...countryFieldProps}
              data-testid={"forecast-country-select"}
              placeholder="Select a country"
            />
          </FormField>

          {subSitesFieldProps?.type === "checkboxes" && (
            <div className={style["sub-sites_container"]}>
              {subSitesFieldProps.items.map((node) => (
                <div key={`site-id-${node.value}`} className={style["sub-sites_container__element"]}>
                  <MmCheckbox
                    cid={`energy-plot-${energyPlot.id}-site-${node.value}`}
                    data-testid={`forecast-zone-${node.value}`}
                    value={node.value?.toString() ?? ""}
                    name={`siteIds-${energyPlot.id}`}
                    checked={subSitesFieldProps.isChecked(node.value)}
                    toggleHandler={(input) => {
                      subSitesFieldProps.onToggle(input.value, input.checked);
                    }}
                  />
                  <label htmlFor={`energy-plot-${energyPlot.id}-site-${node.value}`}>
                    {generateSiteLabel(node.value?.toString() ?? "Unknown")}
                  </label>
                </div>
              ))}
            </div>
          )}

          {energyPlot.groups.map((group, index) => (
            <EnergyGroupSettings
              groupIndex={index}
              forecastType={energyPlot.forecastType}
              availableModelNodes={availableModelNodesByGroup[index] || []}
              key={group.model}
              energyPlotGroup={group}
              updateEnergyGroup={handleGroupUpdate}
              removeGroup={energyPlot.groups.length > 1 ? handleGroupRemove : undefined}
            />
          ))}
        </Section>
      </div>
      <ButtonBar align="right" className="layer-stack-actions">
        <Button icon="add" onClick={toggleAddGroupModal} disabled={!hasAvailableModels}>
          {t`Add Source`}
        </Button>
      </ButtonBar>

      <AddEnergyGroupModal
        forecastType={energyPlot.forecastType}
        availableModelNodes={availableModelNodesForAdd}
        isOpen={showAddGroupModal}
        selectedModel={selectedModelToAdd}
        onSelectModel={setSelectedModelToAdd}
        onClose={toggleAddGroupModal}
        onConfirm={confirmAddGroup}
      />
    </>
  );
}
